import React, { useEffect, useState } from 'react'
import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community'
import { isAfter, format } from 'date-fns'
import startCase from 'lodash/startCase'
import { useSelector } from 'react-redux'
import { Link as ReactRouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast
} from '@chakra-ui/react'
import { admins, capabilities, dates } from '@homevest/utils'

import SetFilter from 'components/AgGrid/filters/SetFilter'
import { BaseGrid } from 'components/TailwindUIToolkit'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { LINK_COLOR } from 'constants/colors'
import {
  CollectionsByMonthQuery,
  LiabilitiesRemainingForCollectionsQuery,
  Note_Priorities_Enum,
  useCancelPromiseToPayMutation,
  useCreatePromiseToPayMutation,
  useUpdatePromiseToPayDateMutation,
  useUpupInsertNoteMutation,
  useUpdateCollectionsZendeskLinkMutation
} from 'graphql/generated'
import { hasCapability } from 'lib/admin-perms'
import axios from 'lib/axios'
import { formatMoney } from 'lib/numbers'
import { StoreState } from 'store'
import { Sidepeek } from 'ui'
import PayOrQuitSendModal from './PayOrQuitSendModal'
// import LatePaymentReasonSelect from './LatePaymentReasonSelect'

type Collection = CollectionsByMonthQuery['collections'][number]
type CollectionLiabilitiesRemaining = LiabilitiesRemainingForCollectionsQuery['collections'][number]
export type CollectionRow = {
  collection: Collection
  liabilitiesRemaining?: CollectionLiabilitiesRemaining
}

export const calcProcessingTotal = (payments: Collection['rental']['payments']) =>
  payments.reduce(
    (prev, payment) => prev + (payment.status === 'processing' ? payment.amount : 0),
    0
  )

export const calcRemainingTotal = (
  liabilities: CollectionLiabilitiesRemaining['liabilities_remaining']
) => liabilities.reduce((prev, liability) => prev + liability.amount_remaining, 0)

export const calcOriginalTotal = (
  liabilities: CollectionLiabilitiesRemaining['liabilities_remaining']
) => liabilities.reduce((prev, liability) => prev + liability.original_amount, 0)

const getColDefs = (
  // fetchingLiabilities: boolean,
  setActiveCollectionRow: (row: CollectionRow) => void,
  setIsSidepeekOpen: (open: boolean) => void,
  setIsEditCommentsModalOpen: (open: boolean) => void,
  setIsZendeskLinkModalOpen: (open: boolean) => void,
  setIsConfirmSendPayOrQuitModalOpen: (open: boolean) => void
): ColDef<CollectionRow>[] => [
  {
    colId: 'address',
    headerName: 'rental',
    width: 200,
    floatingFilter: true,
    valueGetter: (d) => d.data?.collection.rental.portfolio_home?.home.address.display_line_1,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const rentalData = params.data?.collection.rental
      const address = rentalData?.portfolio_home?.home.address.display_line_1
      const state = rentalData?.portfolio_home?.home.address.state
      const city = rentalData?.portfolio_home?.home.address.city
      const fullAddress = [address, city, state].filter(Boolean).join(', ')
      return (
        <Link
          isExternal
          color={LINK_COLOR}
          as={ReactRouterLink}
          to={`/rent-roll/${rentalData?.id}`}
        >
          {fullAddress}
        </Link>
      )
    }
  },
  {
    colId: 'tenant_name',
    headerName: 'Primary Tenant',
    floatingFilter: true,
    valueGetter: (d) => {
      const primaryTenant = d.data?.collection.rental.rental_users.at(0)?.user
      return [primaryTenant?.first_name, primaryTenant?.last_name].filter(Boolean).join(' ')
    },
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const primaryUserId = params.data?.collection.rental.rental_users.at(0)?.user.id
      return (
        <Link isExternal color={LINK_COLOR} as={ReactRouterLink} to={`/users/${primaryUserId}`}>
          {params.value}
        </Link>
      )
    }
  },
  {
    colId: 'state',
    headerName: 'State',
    filter: SetFilter,
    filterParams: {
      badgeOptions: { size: 'xs', formatFn: (val: string) => val }
    },
    valueGetter: (d) => d.data?.collection.rental.portfolio_home?.home.address.state,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      return mapContentToBadge(params.value, { formatFn: (value) => value, size: 'xs' })
    }
  },
  {
    colId: 'fund',
    headerName: 'Fund',
    filter: SetFilter,
    filterParams: {
      badgeOptions: { size: 'xs', formatFn: (val: string) => val }
    },
    valueGetter: (d) =>
      d.data?.collection.rental.portfolio_home?.most_recent_llc_property?.llc?.fund_llcs.at(0)?.fund
        .abbreviation,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      return mapContentToBadge(params.value, { formatFn: (value) => value, size: 'xs' })
    }
  },
  {
    colId: 'moveOutDate',
    headerName: 'Move Out Date',
    floatingFilter: true,
    filter: 'date',
    valueGetter: (d) => d.data?.collection.rental.move_out_date
  },
  {
    colId: 'in_evictions',
    headerName: 'Eviction',
    floatingFilter: true,
    valueGetter: (d) => d.data?.collection.rental.evictions.at(0)?.status,
    valueFormatter: ({ value }) => {
      if (!value) {
        return ''
      }
      return `Yes - ${startCase(value)}`
    }
  },
  {
    colId: 'current_balance',
    headerName: 'Current Balance',
    floatingFilter: true,
    filter: 'number',
    valueGetter: (d) => d.data?.collection.rental.current_balance?.aggregate_balance_after,
    valueFormatter: ({ value }) => formatMoney(value, 2, '$')
  },
  {
    colId: 'processing',
    headerName: 'processing',
    floatingFilter: true,
    filter: 'number',
    valueGetter: ({ data }) => {
      return calcProcessingTotal(data?.collection.rental.payments ?? [])
    },
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      return formatMoney(params.value, 2, '$')
    }
  },
  {
    colId: 'rent',
    headerName: 'Monthly Rent',
    floatingFilter: true,
    filter: 'number',
    valueGetter: (d) => d.data?.collection.rental.rental_agreement_histories.at(0)?.rent,
    valueFormatter: ({ value }) => formatMoney(value, 0, '$')
  },
  {
    colId: 'most_recent_note',
    headerName: 'Most Recent Note',
    floatingFilter: true,
    minWidth: 300,
    valueGetter: (d) => {
      const note = d.data?.collection.rental.notes.at(0)
      if (!note) {
        return
      }
      const noteDate = new Date(note.created_at)
      return `[${format(noteDate, 'yyyy-MM-dd')}] ${note.note}`
    },
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const note = params.data?.collection.rental.notes.at(0)
      if (!note) {
        return ''
      }
      const noteDate = new Date(note.created_at)
      const formattedNoteDate = `[${format(noteDate, 'yyyy-MM-dd')}] `
      return (
        <div>
          <span className='font-semibold'>{formattedNoteDate}</span>
          {note.note}
        </div>
      )
    }
  },
  {
    colId: 'most_recent_pay_or_quit',
    headerName: 'Most Recent Pay or Quit',
    floatingFilter: true,
    minWidth: 300,
    valueGetter: (d) => {
      const payOrQuitNote = d.data?.collection.rental.notes.find(
        (note) =>
          note.note.includes('Pay or Quit') &&
          note.created_by_admin_id === admins.ENGINEERING_ADMIN_ID
      )
      if (!payOrQuitNote) {
        return
      }
      return payOrQuitNote.note
    },
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const payOrQuitNote = params.data?.collection.rental.notes.find(
        (note) =>
          note.note.includes('Pay or Quit') &&
          note.created_by_admin_id === admins.ENGINEERING_ADMIN_ID
      )
      if (!payOrQuitNote) {
        return ''
      }
      return payOrQuitNote.note
    }
  },
  {
    colId: 'send_pay_or_quit',
    headerName: 'Send New Pay or Quit',
    width: 200,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const balance = params.data?.collection.rental.current_balance?.aggregate_balance_after
      if (balance <= 0) {
        return
      }

      return (
        <Button
          size='xs'
          colorScheme='red'
          variant='outline'
          onClick={() => {
            setActiveCollectionRow(params.data!)
            setIsConfirmSendPayOrQuitModalOpen(true)
          }}
        >
          Send Pay or Quit
        </Button>
      )
    }
  },
  {
    headerName: 'Promise to Pay',
    filter: SetFilter,
    filterParams: {
      badgeOptions: { size: 'xs', formatFn: (val: string) => val }
    },
    width: 400,
    valueGetter: ({ data }) => {
      if (!data) {
        return
      }

      const activePromise = data.collection.collection_payment_promises.at(0)
      const isFuturePromise = isAfter(new Date(activePromise?.promise_to_pay_date), new Date())
      const isPaid = data.collection.rental.current_balance?.aggregate_balance_after <= 0

      if (!activePromise) {
        return 'No PTP'
      }

      if (isFuturePromise) {
        return 'Future PTP'
      }

      return isPaid ? 'Paid PTP' : 'Overdue PTP'
    },
    cellRenderer: ({ data }: ICellRendererParams<CollectionRow>) => {
      /*
      if (fetchingLiabilities || !data || !data.liabilitiesRemaining) {
        return <Spinner />
      }

      const remainingTotal = calcRemainingTotal(data.liabilitiesRemaining.liabilities_remaining)

      const isPaid = data.liabilitiesRemaining.liabilities_remaining.every(
        (l) => l.amount_remaining === 0
      )

      const processingTotal = calcProcessingTotal(data.collection.rental.payments)

      if (isPaid) {
        return '✅ Paid'
      }

      const isPaidProcessing = processingTotal >= remainingTotal

      if (isPaidProcessing) {
        return `✅ Paid (${formatMoney(
          Math.min(processingTotal, remainingTotal),
          2,
          '$'
        )} processing)`
      }
      */

      if (!data) {
        return <Spinner />
      }

      const activePromise = data.collection.collection_payment_promises.at(0)
      const isFuturePromise = isAfter(new Date(activePromise?.promise_to_pay_date), new Date())
      const isPaid = data.collection.rental.current_balance?.aggregate_balance_after <= 0

      const descriptionText = isFuturePromise ? (
        'Promised to pay on'
      ) : (
        <>
          {!isPaid && (
            <>
              <span className='font-semibold text-red-500'>Overdue</span> -{' '}
            </>
          )}
          Promised to pay on
        </>
      )

      if (!!activePromise) {
        return (
          <HStack gap='4'>
            <Button
              className={`text-wrap ${isFuturePromise ? '' : 'px-2 py-3'}`}
              size='xs'
              colorScheme='yellow'
              variant='outline'
              onClick={() => {
                setActiveCollectionRow(data)
                setIsSidepeekOpen(true)
              }}
            >
              Edit Promise
            </Button>
            <Text>
              {descriptionText} {activePromise.promise_to_pay_date}
            </Text>
          </HStack>
        )
      }

      return (
        <Button
          className='my-2 text-wrap'
          size='xs'
          colorScheme='teal'
          variant='outline'
          onClick={() => {
            setActiveCollectionRow(data)
            setIsSidepeekOpen(true)
          }}
        >
          Enter Promise to Pay
        </Button>
      )
    }
  },
  {
    colId: 'comments',
    headerName: 'Comments',
    width: 200,
    valueGetter: (d) => d.data?.collection.note?.note,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => {
      const onClick = () => {
        setActiveCollectionRow(params.data!)
        setIsEditCommentsModalOpen(true)
      }

      if (params.value) {
        return <Text onClick={onClick}>{params.value}</Text>
      }

      return (
        <Button
          className='my-2 text-wrap'
          size='xs'
          colorScheme='teal'
          variant='outline'
          onClick={onClick}
        >
          Add Comment
        </Button>
      )
    },
    floatingFilter: true
  },
  {
    colId: 'zendesk_link',
    headerName: 'zendesk_link',
    floatingFilter: true,
    width: 200,
    valueGetter: (d) => d.data?.collection.zendesk_link,
    cellRenderer: (params: ICellRendererParams<CollectionRow>) => (
      <Box
        onDoubleClick={() => {
          setActiveCollectionRow(params.data!)
          setIsZendeskLinkModalOpen(true)
        }}
      >
        {params.value ? (
          <Link className='text-wrap' color={LINK_COLOR} isExternal href={params.value}>
            {params.value}
          </Link>
        ) : (
          <Text className='italic text-gray-400'>Double-click to add/edit link</Text>
        )}
      </Box>
    )
  },
  // Hidden columns for CSV export
  {
    colId: 'originalBalance',
    headerName: 'original balance',
    hide: true,
    valueGetter: ({ data }) =>
      data?.liabilitiesRemaining?.liabilities_remaining.reduce(
        (acc, l) => acc + l.original_amount,
        0
      )
  },
  {
    colId: 'collectionMonthYear',
    headerName: 'monthYear',
    hide: true,
    valueGetter: (d) => d.data?.collection.month
  },
  {
    colId: 'rentalId',
    headerName: 'rental id',
    hide: true,
    valueGetter: (d) => d.data?.collection.rental.id
  },
  {
    colId: 'occupancyDate',
    headerName: 'Occupancy Date',
    valueGetter: (d) => d.data?.collection.rental.occupancy_date,
    hide: true
  },
  {
    colId: 'collectionId',
    headerName: 'collection id',
    hide: true,
    valueGetter: (d) => d.data?.collection.id
  },
  {
    colId: 'promiseToPayDate',
    headerName: 'promise to pay date',
    hide: true,
    valueGetter: (d) => d.data?.collection.collection_payment_promises?.at(0)?.promise_to_pay_date
  }
]

const Grid = ({
  data = [],
  refetchData,
  onGridReady
}: {
  data?: CollectionRow[]
  refetchData?: () => void
  onGridReady: (evt: GridReadyEvent) => void
}) => {
  const [activeCollectionRow, setActiveCollectionRow] = useState<CollectionRow | null>(null)
  const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false)
  const [isZendeskLinkModalOpen, setIsZendeskLinkModalOpen] = useState(false)
  const [isSidepeekOpen, setIsSidepeekOpen] = useState(false)
  const [isConfirmSendPayOrQuitModalOpen, setIsConfirmSendPayOrQuitModalOpen] = useState(false)
  const admin = useSelector((state: StoreState) => state.admin)
  const canSendPayOrQuit = hasCapability(admin, capabilities.CAPABILITY_TYPES.PAY_OR_QUIT_SENDER)

  const activePromise = activeCollectionRow?.collection.collection_payment_promises.at(0)

  return (
    <>
      <div className='h-[70vh] rounded border'>
        <BaseGrid<CollectionRow>
          rowData={data}
          onGridReady={onGridReady}
          columns={getColDefs(
            // fetchingLiabilities,
            setActiveCollectionRow,
            setIsSidepeekOpen,
            setIsEditCommentsModalOpen,
            setIsZendeskLinkModalOpen,
            setIsConfirmSendPayOrQuitModalOpen
          )}
          rowAutoHeight={true}
          defaultColDefOverrides={{
            width: 150,
            resizable: true,
            wrapHeaderText: true
          }}
        />
      </div>
      <Sidepeek
        isOpen={isSidepeekOpen}
        onClose={() => {
          setIsSidepeekOpen(false)
          setActiveCollectionRow(null)
        }}
      >
        {!activePromise && (
          <CreatePromiseToPay
            onCreate={() => {
              setIsSidepeekOpen(false)
              setActiveCollectionRow(null)
              refetchData?.()
            }}
            collectionRow={activeCollectionRow!}
          />
        )}
        {!!activePromise && (
          <EditPromiseToPay
            promiseToPay={activePromise}
            onAction={() => {
              setIsSidepeekOpen(false)
              setActiveCollectionRow(null)
              refetchData?.()
            }}
            collectionRow={activeCollectionRow!}
          />
        )}
      </Sidepeek>
      <EditFieldModal
        title='Edit Comments'
        collectionId={activeCollectionRow?.collection.id}
        text={activeCollectionRow?.collection.note?.note}
        isOpen={isEditCommentsModalOpen}
        onClose={() => {
          setIsEditCommentsModalOpen(false)
          setActiveCollectionRow(null)
        }}
        onSubmit={() => refetchData?.()}
        SubmitButton={CommentSubmitButton}
      />
      <EditFieldModal
        title='Zendesk Link'
        collectionId={activeCollectionRow?.collection.id}
        text={activeCollectionRow?.collection.zendesk_link ?? undefined}
        isOpen={isZendeskLinkModalOpen}
        onClose={() => {
          setIsZendeskLinkModalOpen(false)
          setActiveCollectionRow(null)
        }}
        onSubmit={() => refetchData?.()}
        SubmitButton={ZendeskLinkSubmitButton}
      />
      {activeCollectionRow && (
        <PayOrQuitSendModal
          addressLine1={
            activeCollectionRow.collection.rental.portfolio_home?.home.address.display_line_1
          }
          balance={activeCollectionRow.collection.rental.current_balance?.aggregate_balance_after}
          canSend={canSendPayOrQuit}
          isOpen={isConfirmSendPayOrQuitModalOpen}
          onClose={() => {
            setIsConfirmSendPayOrQuitModalOpen(false)
            setActiveCollectionRow(null)
          }}
          rentalId={activeCollectionRow.collection.rental.id}
          state={activeCollectionRow.collection.rental.portfolio_home?.home.address.state ?? ''}
        />
      )}
    </>
  )
}

const EditPromiseToPay = ({
  promiseToPay,
  collectionRow,
  onAction
}: {
  promiseToPay: Collection['collection_payment_promises'][number]
  collectionRow: CollectionRow
  onAction: () => void
}) => {
  const [editing, setEditing] = useState(false)
  const [promiseToPayDate, setPromiseToPayDate] = useState(promiseToPay.promise_to_pay_date)
  const [customNote, setCustomNote] = useState('')

  const [, cancelPromiseToPay] = useCancelPromiseToPayMutation()
  const [, updatePromiseToPayDate] = useUpdatePromiseToPayDateMutation()
  const [, createNote] = useUpupInsertNoteMutation()

  const adminId = useSelector((store: StoreState) => store.admin.id)

  const cancel = async () => {
    const res = await cancelPromiseToPay({
      promiseId: promiseToPay.id,
      canceledAt: new Date().toISOString()
    })

    const defaultNote = `Canceled promise to pay collection for month of ${collectionRow.collection.month}`

    await createNote({
      note: customNote ? defaultNote + ` [${customNote}]` : defaultNote,
      created_by_admin_id: adminId,
      resource_id: collectionRow.collection.rental.id,
      resource_type: 'rentals',
      priority: Note_Priorities_Enum.Default
    })

    if (res.error) {
      console.error(res.error)
      return
    }

    onAction()
  }

  const update = async () => {
    await updatePromiseToPayDate({ promiseId: promiseToPay.id, promiseToPayDate })

    const defaultNote = `Updated promise to pay collection for month of ${collectionRow.collection.month}`

    await createNote({
      note: customNote ? defaultNote + `[${customNote}]` : defaultNote,
      created_by_admin_id: adminId,
      resource_id: collectionRow.collection.rental.id,
      resource_type: 'rentals',
      priority: Note_Priorities_Enum.Default
    })

    setEditing(false)
    onAction()
  }

  return (
    <Stack spacing={4}>
      <CollectionDetails collectionRow={collectionRow} />
      <Text textDecor='underline'>Existing Promise to Pay</Text>
      <Text>Promise to Pay Date: {promiseToPay.promise_to_pay_date}</Text>
      <Text textDecor='underline'>Custom Note</Text>
      <Textarea value={customNote} onChange={(e) => setCustomNote(e.target.value)} />
      <Button
        onClick={() => setEditing((prev) => !prev)}
        colorScheme={editing ? 'gray' : 'teal'}
        variant='solid'
      >
        {editing ? 'Back' : 'Edit'}
      </Button>
      {!editing && (
        <Button onClick={cancel} colorScheme='red' variant='outline'>
          Cancel Promise
        </Button>
      )}
      {editing && (
        <Stack>
          <Input
            type='date'
            value={promiseToPayDate}
            onChange={(e) => setPromiseToPayDate(e.target.value)}
          />
          <Button onClick={update} colorScheme='teal'>
            Save
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

const CreatePromiseToPay = ({
  onCreate,
  collectionRow
}: {
  onCreate?: () => void
  collectionRow: CollectionRow
}) => {
  const [promiseToPayDate, setPromiseToPayDate] = useState<string>(dates.formatISODate(new Date()))
  const [customNote, setCustomNote] = useState('')

  const [, createPromiseToPay] = useCreatePromiseToPayMutation()
  const [, createNote] = useUpupInsertNoteMutation()

  const adminId = useSelector((store: StoreState) => store.admin.id)

  const submit = async () => {
    const res = await createPromiseToPay({
      collectionId: collectionRow.collection.id,
      promiseToPayDate,
      adminId
    })

    const defaultNote = `Promised to pay collection for month of ${collectionRow.collection.month} by ${promiseToPayDate}`

    await createNote({
      note: customNote ? defaultNote + ` [${customNote}]` : defaultNote,
      created_by_admin_id: adminId,
      resource_id: collectionRow.collection.rental.id,
      resource_type: 'rentals',
      priority: Note_Priorities_Enum.Default
    })

    if (res.error) {
      console.error(res.error)
      return
    }

    onCreate?.()
  }

  return (
    <Stack spacing={4}>
      <CollectionDetails collectionRow={collectionRow} />
      <Text textDecor='underline'>Create a Promise to Pay</Text>
      <Input
        type='date'
        value={promiseToPayDate}
        onChange={(e) => setPromiseToPayDate(e.target.value)}
      />
      <Text textDecor='underline'>Custom Note</Text>
      <Textarea value={customNote} onChange={(e) => setCustomNote(e.target.value)} />
      <Button onClick={submit} colorScheme='teal'>
        Create
      </Button>
    </Stack>
  )
}

const EditFieldModal = ({
  title,
  collectionId,
  text,
  isOpen,
  onClose,
  onSubmit,
  SubmitButton
}: {
  title: string
  collectionId: string
  text?: string
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  SubmitButton: React.FC<{
    collectionId: string
    text?: string
    onSubmit: () => void
  }>
}) => {
  const [textInput, setTextInput] = useState(text)
  useEffect(() => setTextInput(text), [text])

  const onCloseModified = () => {
    onClose()
    setTextInput(undefined)
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseModified}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Textarea value={textInput} onChange={(e) => setTextInput(e.target.value ?? '')} />
        </ModalBody>
        <ModalFooter>
          <SubmitButton
            collectionId={collectionId}
            text={textInput}
            onSubmit={() => {
              onCloseModified()
              onSubmit()
            }}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const CommentSubmitButton: React.FC<{
  collectionId: string
  text?: string
  onSubmit: () => void
}> = ({ collectionId, text, onSubmit }) => {
  const toast = useToast()
  const adminId = useSelector((store: StoreState) => store.admin.id)

  return (
    <Button
      size='sm'
      colorScheme='teal'
      onClick={async () => {
        try {
          await axios.post(`/admin/collections/${collectionId}/note`, {
            note: text,
            admin_id: adminId
          })
          onSubmit()
        } catch (err: any) {
          toast({
            title: `Error: ${err.message}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'bottom-right'
          })
        }
      }}
    >
      Submit
    </Button>
  )
}

const ZendeskLinkSubmitButton: React.FC<{
  collectionId: string
  text?: string
  onSubmit: () => void
}> = ({ collectionId, text, onSubmit }) => {
  const toast = useToast()
  const [, updateZendeskLink] = useUpdateCollectionsZendeskLinkMutation()

  return (
    <Button
      size='sm'
      colorScheme='teal'
      onClick={async () => {
        try {
          updateZendeskLink({ collectionId, zendeskLink: text ?? null })
          onSubmit()
        } catch (err: any) {
          toast({
            title: `Error: ${err.message}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'bottom-right'
          })
        }
      }}
    >
      Submit
    </Button>
  )
}

const CollectionDetails = ({ collectionRow }: { collectionRow: CollectionRow }) => {
  const rental = collectionRow.collection.rental

  return (
    <Stack>
      <Text fontWeight='bold' textDecor='underline'>
        Collection Data
      </Text>
      <Text>
        Rental Address:{' '}
        <a target='_blank' href={process.env.PUBLIC_URL + '/rent-roll/' + rental.id}>
          {rental.portfolio_home?.home.address.display_line_1},{' '}
          {rental.portfolio_home?.home.address.city}, {rental.portfolio_home?.home.address.state}
        </a>
      </Text>
      <Text>
        Current Balance: {formatMoney(rental.current_balance?.aggregate_balance_after, 2, '$')}
      </Text>
    </Stack>
  )
}

export default Grid
